/**
 * @file ModalComponent.tsx
 * @description
 * -----------------------------------------------------
 * Componente modal
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-04-02
 * @author Correcta
 * @copyright © Correcta
 * @license Private
 * */

"use client";

import React, { useCallback, useEffect, useRef, useTransition } from "react";
import { X, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { FocusTrap } from "focus-trap-react";
import ButtonComponent from "@/components/ui/ButtonComponent";
import { ModalProps } from "@/types/modals";
import ImageComponent from "@/components/ui/ImageComponent";
import { BUTTON_STATUS, TYPE_BUTTON } from "@/constants/button";
import { BUTTON_LABELS } from "@/constants/buttonLabels";
import { useTranslations } from "next-intl";
import FormComponent from "@/components/ui/FormComponent";
import SubmitButtonComponent from "../ui/SubmitButtonComponent";

const ModalComponent = <TFormData = undefined,>({
  title,
  description,
  isOpen,
  onClose,
  children,
  buttonConfirm = false,
  buttonCancel = true,
  buttonSubmit = false,
  buttonSubmitIcon,
  buttonConfirmIcon,
  onConfirm,
  formAction,
  buttonConfirmText = BUTTON_LABELS.CONFIRM,
  buttonCancelText = BUTTON_LABELS.CANCEL,
  buttonSubmitText = BUTTON_LABELS.SUBMIT,
  formData,
  confirmStatus = BUTTON_STATUS.PRIMARY,
  imageSource,
  isSubmitEnabled,
  customModalFooter,
  closeOnOutsideClick = true,
  requiredText = false,
  useNativeForm = true,
  contentClassName,
}: ModalProps<TFormData>) => {
  const handleClose = useCallback(() => onClose(), [onClose]);
  const contentRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [pending, startTransition] = useTransition();
  const labelT = useTranslations("Labels");
  const ariaLabelT = useTranslations("AriaLabel");

  const handleConfirm = useCallback(
    (e?: React.FormEvent | React.MouseEvent) => {
      startTransition(() => {
        if (onConfirm) onConfirm(e);
      });
    },
    [onConfirm],
  );

  // Manejo del clic fuera del modal
  useEffect(() => {
    if (!closeOnOutsideClick || !isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        contentRef.current &&
        !contentRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    // Pequeño delay para evitar que se cierre inmediatamente al abrir
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, closeOnOutsideClick, isOpen]);

  // Manejo de la tecla Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  const renderFooter = () => {
    const isFooterEmpty =
      !customModalFooter && !buttonConfirm && !buttonCancel && !buttonSubmit;

    if (isFooterEmpty) return null;

    return (
      <section className="modal__footer">
        {customModalFooter ? (
          customModalFooter
        ) : (
          <>
            {buttonSubmit && formAction && (
              <SubmitButtonComponent
                formData={formData}
                buttonLabel={buttonSubmitText}
                status={BUTTON_STATUS.PRIMARY}
                iconPosition="left"
                className={"btn--full"}
                isButtonEnabled={isSubmitEnabled}
              >
                {buttonSubmitIcon ? buttonSubmitIcon : <Plus size={20} />}
              </SubmitButtonComponent>
            )}
            {buttonConfirm && (
              <ButtonComponent
                status={confirmStatus}
                type={TYPE_BUTTON}
                onClick={handleConfirm}
                label={pending ? BUTTON_LABELS.LOADING : buttonConfirmText}
                className={"btn--full"}
                iconPosition="left"
              >
                {buttonConfirmIcon}
              </ButtonComponent>
            )}
            {buttonCancel && (
              <ButtonComponent
                status={BUTTON_STATUS.NO_OUTLINE}
                type={TYPE_BUTTON}
                className={"btn--full"}
                onClick={handleClose}
                label={buttonCancelText}
              />
            )}
          </>
        )}
      </section>
    );
  };

  const renderHeader = () => (
    <>
      <section className="modal__close">
        <span className="">
          <ButtonComponent
            ref={closeButtonRef}
            type={TYPE_BUTTON}
            status={BUTTON_STATUS.TOPBAR}
            onClick={handleClose}
            ariaLabel={ariaLabelT("closeModal")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleClose();
              }
            }}
          >
            <X size={20} aria-hidden="true" focusable="false" />
          </ButtonComponent>
        </span>
      </section>
      {imageSource && (
        <ImageComponent
          className="modal__image"
          src={imageSource}
          alt="warningImage"
        />
      )}
      <section className="modal__header">
        <h3>{title}</h3>
        {description && <p>{description}</p>}
      </section>
    </>
  );

  return (
    <motion.article
      initial={{ opacity: 0 }}
      animate={{ opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.2 }}
      exit={{ opacity: 0 }}
      className={`modal ${isOpen ? "modal-overlay" : ""}`}
    >
      {isOpen && (
        <FocusTrap
          active={isOpen}
          focusTrapOptions={{
            escapeDeactivates: false,
            clickOutsideDeactivates: false,
            allowOutsideClick: true,
            returnFocusOnDeactivate: true,
            initialFocus: closeButtonRef.current || undefined,
          }}
        >
          <section
            className={`modal__content ${contentClassName ? contentClassName : ""}`}
            ref={contentRef}
            role="dialog"
            aria-modal="true"
            aria-label={
              typeof title === "string" && title.trim() !== ""
                ? title
                : ariaLabelT("modal")
            }
          >
            {buttonSubmit && formAction ? (
              <FormComponent<TFormData>
                formAction={formAction}
                formClassName="form"
                buttonSubmit={false}
                useNativeForm={useNativeForm}
              >
                <section className="modal-body">
                  {renderHeader()}
                  {children}
                  {requiredText && (
                    <p className="modal__required-text">
                      {labelT("labelRequired")}
                    </p>
                  )}
                  {renderFooter()}
                </section>
              </FormComponent>
            ) : (
              <section className="modal-body">
                {renderHeader()}
                {children}
                {requiredText && (
                  <p className="modal__required-text">
                    {labelT("labelRequired")}
                  </p>
                )}
                {renderFooter()}
              </section>
            )}
          </section>
        </FocusTrap>
      )}
    </motion.article>
  );
};

export default ModalComponent;
